import React, { useEffect, useState, KeyboardEvent } from "react";
import { styled, Theme } from "@mui/material/styles";
import { Pagination, PaginationItem, TextField, Box, Typography } from "@mui/material";
import { useIntl } from "react-intl";
import ButtonComponent from "./common/ButtonComponent";

const PaginationContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flex: "1 1 auto",
});

const GoToPageContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  flex: "0 0 auto",
});

const StyledPagination = styled(Pagination)(({ theme }) => ({
  "& .Mui-selected": {
    backgroundColor: "transparent !important",
    color: theme.palette?.primary?.main + " !important",
    borderRadius: "0 !important",
  },
  "& .MuiPaginationItem-root": {
    "&:hover": {
      backgroundColor: "transparent",
      color: theme.palette?.primary?.main,
      borderRadius: "0",
    },
  },
}));

const inputStyles = {
  width: "55px",
  "& .MuiInputBase-root": {
    height: "23px !important",
  },
  "& .MuiInputBase-root.Mui-focused fieldset": {
    borderColor: (theme: Theme) => theme.palette?.focusColor?.main,
  },
};

export interface ProductPaginationBarProps {
  page: number;
  pageCount: number;
  loadedPageCount?: number;
  onPageChange: (event: React.ChangeEvent<unknown>, page: number) => void;
}

/**
 * Shared MUI pagination + “go to page” controls used by the main product grid
 * (`ProductListPagination`) and Find Products.
 */
const ProductPaginationBar: React.FC<ProductPaginationBarProps> = ({
  page,
  pageCount,
  loadedPageCount,
  onPageChange,
}) => {
  const intl = useIntl();
  const [goToPage, setGoToPage] = useState("");
  const safeCount = Math.max(1, pageCount);
  const effectiveLoadedCount = loadedPageCount ?? pageCount;
  const shouldDisableGoToButton =
    goToPage === "" ||
    Number(goToPage) > pageCount ||
    Number(goToPage) < 1 ||
    pageCount < 1 ||
    Number(goToPage) > effectiveLoadedCount;

  useEffect(() => {
    setGoToPage(String(page));
  }, [page]);

  const buttonStyles = {
    display: "flex",
    height: "23px",
    minWidth: "10px",
    width: "10px",
    padding: "0",
    cursor: shouldDisableGoToButton ? "default" : "pointer",
  };

  const handleGoToPage = () => {
    const pageNum = parseInt(goToPage, 10);
    if (pageCount >= 1 && pageNum >= 1 && pageNum <= pageCount) {
      onPageChange({} as React.ChangeEvent<unknown>, pageNum);
    }
  };

  const handleGoToKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleGoToPage();
    }
  };

  return (
    <>
      <PaginationContainer>
        <StyledPagination
          page={page}
          count={safeCount}
          onChange={onPageChange}
          siblingCount={1}
          boundaryCount={1}
          renderItem={(item) => (
            <PaginationItem
              {...item}
              disabled={
                item.type === "page" && item.page !== null && item.page > effectiveLoadedCount
                  ? true
                  : item.disabled
              }
            />
          )}
        />
      </PaginationContainer>
      <GoToPageContainer>
        <Typography variant="body2">
          {intl.formatMessage({
            id: "productListSwitch.goToPage",
            defaultMessage: "Go to page",
          })}
        </Typography>
        <TextField
          size="small"
          value={goToPage}
          onChange={(e) => setGoToPage(e.target.value)}
          onKeyDown={handleGoToKeyDown}
          sx={inputStyles}
        />
        <ButtonComponent
          color="primary"
          variant="contained"
          onClick={handleGoToPage}
          style={buttonStyles}
          justifyContent="center"
          disabled={shouldDisableGoToButton}
        >
          {intl.formatMessage({
            id: "productListSwitch.goButton",
            defaultMessage: "Go",
          })}
        </ButtonComponent>
      </GoToPageContainer>
    </>
  );
};

export default ProductPaginationBar;
